import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, Bell, BellOff, Glasses, Loader2, Moon, RefreshCw } from 'lucide-react';
import { HudDevice } from '../../services/hud';
import { getGlassesArmsLabel } from '../../services/n8n';
import { formatCountdown, formatTime, tabHref, toMs } from './hudShared';

const OFFLINE_MS = 90_000;
const STALE_MS = 30_000;
const LOW_BATTERY = 15;

type Tone = 'ok' | 'amber' | 'red';

function deviceStatus(d: HudDevice, now: number): { label: string; tone: Tone } {
  const name = d.role === 'primary' ? 'Lead glasses' : 'Viewer glasses';
  const last = toMs(d.lastSyncAt);
  const age = now - last;
  if (!last || age > OFFLINE_MS) {
    return { label: `${name} offline${last ? ` since ${formatTime(d.lastSyncAt)}` : ''}`, tone: 'red' };
  }
  const battery = typeof d.battery === 'number' ? d.battery : null;
  const issues: string[] = [];
  let tone: Tone = 'ok';
  if (battery !== null && battery <= LOW_BATTERY && !d.charging) {
    issues.push(`battery ${battery}%`);
    tone = 'red';
  }
  const arms = getGlassesArmsLabel(d.arms);
  if (!arms.ok) {
    issues.push(arms.label.toLowerCase());
    if (tone === 'ok') tone = 'amber';
  }
  if (age > STALE_MS) {
    issues.push('sync delayed');
    if (tone === 'ok') tone = 'amber';
  }
  if (issues.length) return { label: `${name}: ${issues.join(', ')}`, tone };
  return {
    label: `${name} ok${battery !== null ? `, ${battery}%` : ''}${d.charging ? ', charging' : ''}`,
    tone: 'ok',
  };
}

function formatAgo(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 5) return 'updated just now';
  if (s < 60) return `updated ${s}s ago`;
  return `updated ${Math.floor(s / 60)}m ago`;
}

interface HudStatusStripProps {
  devices: HudDevice[];
  now: number;
  lastUpdated: number | null;
  refreshing: boolean;
  onRefresh: () => void;
  quiet: boolean;
  savingQuiet: boolean;
  onQuiet: (minutes: number | null) => void;
  onResume: () => void;
  chime: boolean;
  onToggleChime: () => void;
}

const QUIET_OPTIONS: { label: string; minutes: number | null }[] = [
  { label: '10 min', minutes: 10 },
  { label: '20 min', minutes: 20 },
  { label: 'Until I turn it off', minutes: null },
];

export function HudStatusStrip({
  devices, now, lastUpdated, refreshing, onRefresh, quiet, savingQuiet, onQuiet, onResume, chime, onToggleChime,
}: HudStatusStripProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const sorted = [...devices].sort((a, b) =>
    (a.role === 'primary' ? 0 : 1) - (b.role === 'primary' ? 0 : 1) || (a.slot || '').localeCompare(b.slot || ''));

  return (
    <header className="bg-white border border-[#e2e8f0] rounded-2xl px-4 py-3 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary shrink-0">
            <Glasses size={16} />
          </div>
          <h1 className="text-lg font-bold tracking-tight text-navy">Facilitator HUD</h1>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => (quiet ? onResume() : setMenuOpen(o => !o))}
              disabled={savingQuiet}
              aria-pressed={quiet}
              className={`flex items-center gap-1.5 rounded-xl h-8 px-3 border text-xs font-bold transition-colors disabled:opacity-60 ${
                quiet
                  ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title={quiet ? 'Resume hints on the glasses' : 'Pause hints on the glasses'}
            >
              {savingQuiet ? <Loader2 size={14} className="animate-spin" /> : <Moon size={14} />}
              {quiet ? 'Quiet on' : 'Quiet'}
            </button>
            {menuOpen && !quiet && (
              <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white border border-[#e2e8f0] rounded-xl shadow-lg py-1">
                <p className="px-3 py-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Pause hints for</p>
                {QUIET_OPTIONS.map(o => (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => { setMenuOpen(false); onQuiet(o.minutes); }}
                    className="w-full text-left px-3 py-2 text-sm text-navy hover:bg-slate-50"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onToggleChime}
            aria-pressed={chime}
            title={chime ? 'Answer chime on' : 'Answer chime off'}
            className={`flex items-center justify-center rounded-xl h-8 w-8 border transition-colors ${
              chime ? 'border-primary/40 bg-primary/5 text-primary' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'
            }`}
          >
            {chime ? <Bell size={14} /> : <BellOff size={14} />}
          </button>

          <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">
            {lastUpdated ? formatAgo(now - lastUpdated) : 'not loaded'}
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            title="Refresh now"
            aria-label="Refresh now"
            className="flex items-center justify-center rounded-lg h-7 w-7 text-slate-400 hover:text-navy hover:bg-slate-50 disabled:opacity-60"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        {sorted.length === 0 ? (
          <span className="text-slate-400">No glasses connected. Start the HUD on the facilitator's laptop.</span>
        ) : sorted.map(d => {
          const s = deviceStatus(d, now);
          if (s.tone === 'ok') return <span key={d.deviceId} className="text-slate-500">{s.label}</span>;
          return (
            <span
              key={d.deviceId}
              className={`flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full ${
                s.tone === 'red' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}
            >
              <AlertTriangle size={12} /> {s.label}
            </span>
          );
        })}
        <Link to={tabHref(location.search, 'devices')} className="ml-auto text-primary font-semibold hover:underline">
          Device details
        </Link>
      </div>
    </header>
  );
}

export function QuietBanner({ quietUntil, now, saving, onResume }: {
  quietUntil?: string | null;
  now: number;
  saving: boolean;
  onResume: () => void;
}) {
  const until = toMs(quietUntil);
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3">
      <Moon size={16} className="text-amber-600 shrink-0" />
      <p className="text-sm font-semibold text-amber-800 tabular-nums">
        Hints paused on the glasses, {until ? `${formatCountdown(until - now)} left` : 'until you turn it off'}
      </p>
      <button
        type="button"
        onClick={onResume}
        disabled={saving}
        className="ml-auto rounded-lg h-8 px-3 bg-white border border-amber-300 text-amber-800 text-xs font-bold hover:bg-amber-100 disabled:opacity-60"
      >
        Resume now
      </button>
    </div>
  );
}
